import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronsDownUp, ChevronsUpDown, CircleDot, CheckCircle, Circle, Clock, XCircle } from 'lucide-react';
import { useDocumentTitle } from 'usehooks-ts';
import { z } from 'zod/v3';
import { trpc } from '@/lib/trpc';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';
import { ControlTree, type ControlTreeRef } from '@/components/compliance/ControlTree';
import { TestsList } from '@/components/compliance/TestsList';
import { AssigneeFilter } from '@/components/compliance/AssigneeFilter';
import { LabelFilter } from '@/components/compliance/LabelFilter';
import { TestTypeFilter } from '@/components/compliance/TestTypeFilter';
import { TestsToolbar, type TestsToolbarStatusOption } from '@/components/compliance/TestsToolbar';
import { controlTreeNodeToTaskDTO } from '@/lib/compliance-status';
import type { ControlTreeNode, TaskDTO } from '@deepguide-ai/types';

const testsSearchSchema = z.object({
  controlRef: z.string().optional(),
  intent: z.enum(['prepare', 'generate']).optional(),
});

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/tests',
)({
  validateSearch: testsSearchSchema,
  component: ScreenataTestsPage,
});

const statusOptions: TestsToolbarStatusOption[] = [
  { value: 'all', label: 'All Status', icon: CircleDot, iconClass: 'text-muted-foreground' },
  { value: 'PASSED', label: 'Passed', icon: CheckCircle, iconClass: 'text-green-600 dark:text-green-400' },
  { value: 'FAILED', label: 'Failed', icon: XCircle, iconClass: 'text-red-600 dark:text-red-400' },
  { value: 'NEEDS_REVIEW', label: 'In Review', icon: Clock, iconClass: 'text-amber-600 dark:text-amber-400' },
  { value: 'IN_PROGRESS', label: 'In Progress', icon: Circle, iconClass: 'text-blue-600 dark:text-blue-400' },
  { value: 'NOT_TESTED', label: 'Not Started', icon: Circle, iconClass: 'text-muted-foreground' },
];

type NecessityMeta = {
  necessity: 'required' | 'common-ask' | 'scope-dependent' | 'optional';
  necessityReason?: string | null;
  necessitySource?: 'type-i-baseline' | 'auditor-ask' | 'scope-selected' | null;
};

type TestOutlineGroup = {
  tests: Array<{
    statusId: string;
    necessity: NecessityMeta['necessity'];
    necessityReason: string | null;
    necessitySource?: NecessityMeta['necessitySource'];
  }>;
};

function ScreenataTestsPage() {
  const navigate = useNavigate();
  const { workspaceId } = Route.useParams();
  const { controlRef, intent } = Route.useSearch();

  useDocumentTitle('Build Evidence Checklist From Auditor Matrix');

  // View mode
  const [viewMode, setViewMode] = useState<'controls' | 'list'>('controls');
  const [packView, setPackView] = useState<'minimum' | 'all'>('minimum');

  // Filters
  const [searchQuery, setSearchQuery] = useState(controlRef ?? '');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('all');
  const [selectedTestType, setSelectedTestType] = useState<string>('all');
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 200);

  useEffect(() => {
    if (!controlRef) return;
    setViewMode('controls');
    setSearchQuery(controlRef);
  }, [controlRef]);

  // ControlTree ref for expand/collapse
  const controlTreeRef = useRef<ControlTreeRef>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  useEffect(() => {
    const hasActiveFilter =
      debouncedSearch.trim().length > 0 ||
      selectedStatus !== 'all' ||
      selectedAssignee !== 'all' ||
      selectedLabelIds.length > 0;
    setIsAllExpanded(hasActiveFilter);
  }, [debouncedSearch, selectedStatus, selectedAssignee, selectedLabelIds]);

  const handleToggleExpandAll = useCallback(() => {
    if (controlTreeRef.current) {
      if (controlTreeRef.current.isAllExpanded) {
        controlTreeRef.current.collapseAll();
        setIsAllExpanded(false);
      } else {
        controlTreeRef.current.expandAll();
        setIsAllExpanded(true);
      }
    }
  }, []);

  // Fetch controls tree — workspace-specific SOC2 framework is auto-discovered
  const treeQuery = useQuery({
    ...trpc.compliance.getControlsTree.queryOptions({
      labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
    }),
    placeholderData: (previousData) => (debouncedSearch.trim() ? undefined : previousData),
  });

  // Fetch necessity metadata from Test Plan Outline
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  );
  const outlineQuery = useQuery({
    ...trpc.complianceProgram.getTestPlanOutline.queryOptions({
      programId: programQuery.data?.id ?? '',
    }),
    enabled: !!programQuery.data?.id,
  });
  const isTypeI = (programQuery.data?.auditType ?? 'TYPE_I') === 'TYPE_I';

  const tree = useMemo(
    () => treeQuery.data?.tree || [],
    [treeQuery.data?.tree],
  );

  // Flatten to categories (skip framework level)
  const categories = useMemo(
    () => (tree.length > 0 ? tree[0].children || [] : []),
    [tree],
  );

  const necessityByStatusId = useMemo<Record<string, NecessityMeta>>(() => {
    const map: Record<string, NecessityMeta> = {};
    const controls = (outlineQuery.data?.controls ?? []) as TestOutlineGroup[];
    for (const group of controls) {
      for (const test of group.tests) {
        if (!test?.statusId) continue;
        map[test.statusId] = {
          necessity: test.necessity,
          necessityReason: test.necessityReason,
          necessitySource: test.necessitySource ?? null,
        };
      }
    }
    return map;
  }, [outlineQuery.data?.controls]);

  // Filter by search and status
  const searchLower = debouncedSearch.trim().toLowerCase();
  const filteredTree = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          children: category.children
            ?.map((control) => {
              let filteredTests = control.children || [];
              const controlIdLower = control.controlId?.toLowerCase() ?? '';
              const matchesControlId = searchLower ? controlIdLower.includes(searchLower) : false;

              if (isTypeI && packView === 'minimum') {
                filteredTests = filteredTests.filter((test) => {
                  if (!test.statusId) return false;
                  const meta = necessityByStatusId[test.statusId];
                  return meta?.necessity === 'required';
                });
              }

              if (selectedStatus !== 'all') {
                filteredTests = filteredTests.filter((test) => {
                  if (selectedStatus === 'PASSED') return test.status === 'PASSED';
                  if (selectedStatus === 'FAILED') return test.status === 'FAILED';
                  if (selectedStatus === 'NEEDS_REVIEW') return test.status === 'NEEDS_REVIEW';
                  if (selectedStatus === 'IN_PROGRESS') return test.status === 'IN_PROGRESS';
                  if (selectedStatus === 'NOT_TESTED') return test.status === 'NOT_TESTED' || !test.status;
                  return true;
                });
              }

              if (selectedAssignee !== 'all') {
                filteredTests = filteredTests.filter((test) => {
                  if (selectedAssignee === 'unassigned') {
                    return !test.assignee;
                  }
                  if (selectedAssignee === 'assigned') {
                    return !!test.assignee;
                  }
                  return test.assignee?.id === selectedAssignee;
                });
              }

              if (searchLower) {
                if (!matchesControlId) {
                  filteredTests = filteredTests.filter(
                    (test) =>
                      test.name?.toLowerCase().includes(searchLower) ||
                      test.taskCode?.toLowerCase().includes(searchLower) ||
                      test.assignee?.name?.toLowerCase().includes(searchLower),
                  );
                }
              }

              if (searchLower && !matchesControlId && filteredTests.length === 0) {
                return false;
              }

              if (
                (selectedStatus !== 'all' || selectedAssignee !== 'all' || (isTypeI && packView === 'minimum')) &&
                filteredTests.length === 0
              ) {
                return false;
              }

              return { ...control, children: filteredTests };
            })
            .filter(Boolean) as ControlTreeNode[],
        }))
        .filter((category) => category.children && category.children.length > 0),
    [categories, searchLower, selectedStatus, selectedAssignee, necessityByStatusId, isTypeI, packView],
  );

  const packScopedCategories = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          children: category.children
            ?.map((control) => {
              if (!(isTypeI && packView === 'minimum')) return control;
              const filteredTests = (control.children || []).filter((test) => {
                if (!test.statusId) return false;
                const meta = necessityByStatusId[test.statusId];
                return meta?.necessity === 'required';
              });
              return { ...control, children: filteredTests };
            })
            .filter((control) => (control.children?.length ?? 0) > 0) as ControlTreeNode[],
        }))
        .filter((category) => category.children && category.children.length > 0),
    [categories, isTypeI, packView, necessityByStatusId],
  );

  const treeKey = useMemo(
    () => [searchLower, selectedStatus, selectedAssignee, selectedLabelIds.join(','), packView].join('|'),
    [searchLower, selectedStatus, selectedAssignee, selectedLabelIds, packView],
  );

  // Stats
  const stats = useMemo(() => {
    let totalTests = 0;
    let passed = 0;
    let failed = 0;
    let inReview = 0;
    let notStarted = 0;
    const seenIds = new Set<string>();

    packScopedCategories.forEach((cat) => {
      cat.children?.forEach((control) => {
        control.children?.forEach((test) => {
          if (test.type === 'test' && test.statusId && !seenIds.has(test.statusId)) {
            seenIds.add(test.statusId);
            totalTests++;
            if (test.status === 'PASSED') passed++;
            else if (test.status === 'FAILED') failed++;
            else if (test.status === 'NEEDS_REVIEW') inReview++;
            else notStarted++;
          }
        });
      });
    });

    return { totalTests, passed, failed, inReview, notStarted };
  }, [packScopedCategories]);

  const necessityStats = useMemo(() => {
    const seenIds = new Set<string>();
    let required = 0;
    let optional = 0;

    packScopedCategories.forEach((cat) => {
      cat.children?.forEach((control) => {
        control.children?.forEach((test) => {
          if (test.type !== 'test' || !test.statusId || seenIds.has(test.statusId)) return;
          seenIds.add(test.statusId);
          const necessity = necessityByStatusId[test.statusId]?.necessity;
          if (necessity === 'required') required += 1;
          else optional += 1;
        });
      });
    });

    return { required, optional };
  }, [packScopedCategories, necessityByStatusId]);

  // Flatten filtered tree into TaskDTO[] for list view
  const flatTests = useMemo(() => {
    const seenIds = new Set<string>();
    const tests: TaskDTO[] = filteredTree.flatMap((cat) =>
      (cat.children ?? []).flatMap((control) =>
        (control.children ?? [])
          .filter((t) => t.type === 'test' && t.statusId && !seenIds.has(t.statusId))
          .map((t) => {
            seenIds.add(t.statusId!);
            return controlTreeNodeToTaskDTO(t, control.controlId);
          }),
      ),
    );

    if (selectedTestType !== 'all') {
      return tests.filter((test) => {
        const testType = test.isAdHoc ? 'AD_HOC' : (test.templateType ?? 'GUIDED');
        return testType === selectedTestType;
      });
    }

    return tests;
  }, [filteredTree, selectedTestType]);

  const handleTestClick = (statusId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/tests/$testId',
      params: { workspaceId, testId: statusId },
      search: { from: 'screenata-tests' },
    });
  };

  const passRate = stats.totalTests > 0 ? Math.round((stats.passed / stats.totalTests) * 100) : 0;

  return (
    <>
      <MainHeader>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold">Build evidence checklist from auditor matrix</h1>
          {!treeQuery.isLoading && (
            <span className="text-sm text-muted-foreground">
              {isTypeI && packView === 'minimum'
                ? `${necessityStats.required} required items · ${passRate}% passing`
                : `${stats.totalTests} checklist items · ${passRate}% passing`}
            </span>
          )}
        </div>
      </MainHeader>
      <Main>
        <div className="flex flex-col gap-4 p-4">
          {/* Stats cards */}
          {!treeQuery.isLoading && stats.totalTests > 0 && (
            <div className="grid grid-cols-4 gap-3">
              <button
                type="button"
                onClick={() => setSelectedStatus('PASSED')}
                className="rounded-lg border px-4 py-3 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="text-xs text-muted-foreground">Passed</div>
                <div className="text-2xl font-semibold tabular-nums text-green-600 dark:text-green-400">{stats.passed}</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('FAILED')}
                className="rounded-lg border px-4 py-3 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="text-xs text-muted-foreground">Failed</div>
                <div className="text-2xl font-semibold tabular-nums text-red-600 dark:text-red-400">{stats.failed}</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('NEEDS_REVIEW')}
                className="rounded-lg border px-4 py-3 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="text-xs text-muted-foreground">In Review</div>
                <div className="text-2xl font-semibold tabular-nums text-amber-600 dark:text-amber-400">{stats.inReview}</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('NOT_TESTED')}
                className="rounded-lg border px-4 py-3 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="text-xs text-muted-foreground">Not Started</div>
                <div className="text-2xl font-semibold tabular-nums text-muted-foreground">{stats.notStarted}</div>
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="sticky top-0 pt-2 bg-background z-10">
            {controlRef && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2">
                <div className="text-xs text-blue-700">
                  <p className="font-medium">
                    {intent === 'prepare'
                      ? 'Prepare this policy in Controls.'
                      : intent === 'generate'
                        ? 'Generate this policy from Controls.'
                        : 'Focused on a control from Policies.'}
                  </p>
                  <p className="mt-0.5">
                    Control: <span className="font-mono">{controlRef}</span>
                  </p>
                </div>
              </div>
            )}
            <div className="mb-4 border-b border-border">
              <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'controls' | 'list')}>
                <TabsList className="h-auto p-0 bg-transparent gap-4">
                  <TabsTrigger
                    value="list"
                    className="px-1 pb-2 pt-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent shadow-none data-[state=active]:shadow-none"
                  >
                    Tests
                  </TabsTrigger>
                  <TabsTrigger
                    value="controls"
                    className="px-1 pb-2 pt-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent shadow-none data-[state=active]:shadow-none"
                  >
                    Controls
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <TestsToolbar
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              searchPlaceholder="Search by control ID or checklist item..."
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              statusOptions={statusOptions}
              leftContent={
                <>
                  {viewMode === 'list' && (
                    <TestTypeFilter
                      value={selectedTestType}
                      onChange={setSelectedTestType}
                    />
                  )}
                  <AssigneeFilter
                    value={selectedAssignee}
                    onChange={setSelectedAssignee}
                  />
                  <LabelFilter
                    value={selectedLabelIds}
                    onChange={setSelectedLabelIds}
                  />
                  {isTypeI ? (
                    <div className="flex items-center rounded-md border">
                      <Button
                        variant={packView === 'minimum' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setPackView('minimum')}
                        className="h-8 rounded-r-none"
                      >
                        Required pack
                      </Button>
                      <Button
                        variant={packView === 'all' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setPackView('all')}
                        className="h-8 rounded-l-none"
                      >
                        Show all
                      </Button>
                    </div>
                  ) : null}
                </>
              }
              rightContent={viewMode === 'controls' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleExpandAll}
                  className="h-9 px-3 text-muted-foreground"
                >
                  {isAllExpanded ? (
                    <>
                      <ChevronsDownUp className="h-4 w-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronsUpDown className="h-4 w-4" />
                      Expand All
                    </>
                  )}
                </Button>
              ) : null}
            />

          </div>

          {/* Tree / List / Loading / Empty */}
          {treeQuery.isLoading ? (
            <div className="space-y-1">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-5 w-14 rounded" />
                  <Skeleton className="h-4 flex-1 max-w-[300px]" />
                  <div className="ml-auto flex items-center gap-3">
                    <Skeleton className="h-5 w-12 rounded" />
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-6 w-14 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTree.length === 0 && categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-muted-foreground mb-2">No checklist items yet</div>
              <p className="text-sm text-muted-foreground max-w-md">
                Checklist items appear after matrix-first materialization.
                Complete Step 6 to build the evidence checklist.
              </p>
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-muted-foreground">No checklist items match your filters</div>
            </div>
          ) : viewMode === 'controls' ? (
            <ControlTree
              key={treeKey}
              ref={controlTreeRef}
              nodes={filteredTree}
              onTestClick={handleTestClick}
              hasActiveFilters={
                !!debouncedSearch ||
                selectedStatus !== 'all' ||
                selectedAssignee !== 'all' ||
                selectedLabelIds.length > 0
              }
              initiallyExpanded={
                !!debouncedSearch ||
                selectedStatus !== 'all' ||
                selectedAssignee !== 'all' ||
                selectedLabelIds.length > 0
              }
              necessityByStatusId={necessityByStatusId}
            />
          ) : (
            <TestsList
              tests={flatTests}
              onTestClick={handleTestClick}
              isAdmin={true}
              necessityByStatusId={necessityByStatusId}
            />
          )}
        </div>
      </Main>
    </>
  );
}
